import { useEffect, useLayoutEffect } from "react";
import { installMicrosoftLinkTracking } from "../lib/microsoftConversions";
import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import StickyInquiryRail from "./StickyInquiryRail";
import InlineLeadForm from "./InlineLeadForm";
import '../styles/site-design.css';
import { useSectionMotion } from './useSectionMotion';

// Routes that should NOT render the global "Talk to Engineering" inline form.
// - Home has its own bespoke CTA and would render a duplicate form otherwise.
// - Careers / Sustainability have their own narratives.
// - Contact already has the primary lead form on-page.
// - Energetics / Mounts use the higher-intent RFQ-60 form instead.
const FORM_SKIP_EXACT = new Set(["/"]);
const FORM_SKIP_PREFIXES = [
  "/careers",
  "/sustainability",
  "/contact-us",
  "/energetics",
  "/mounts-mechanical",
];

export default function Layout() {
  const { pathname, hash } = useLocation();
  useSectionMotion(pathname);
  useEffect(() => installMicrosoftLinkTracking(), []);
  useLayoutEffect(() => {
    const header = document.querySelector('[data-testid="site-header"]');
    if (!header) return;
    const measure = () => {
      if (header.querySelector('[data-testid="mobile-menu"]')) return;
      document.documentElement.style.setProperty('--site-header-height', `${header.getBoundingClientRect().height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    window.addEventListener('resize', measure);
    document.fonts.ready.then(measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  // Kill the browser's SPA scroll restoration once, up front.
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Hard scroll reset on every route change. The heavier pages
  // (Energetics / Capabilities / Quality & Testing / Careers) lazy-load
  // large hero media and animated blocks — those mount AFTER the initial
  // scroll reset and can push the layout down, leaving the user "mid-page".
  // We fix this with a staggered sequence of resets that cover:
  //   • the synchronous frame (before paint)
  //   • the next paint frame (after Suspense resolves)
  //   • the next macrotask (after images/decoding)
  //   • a longer settle window (200ms) for hero videos & framer-motion mounts
  useLayoutEffect(() => {
    let userScrolled = false;
    let cancelled = false;
    let anchorFound = false;
    let anchorObserver;
    const markUserScrolled = () => { userScrolled = true; };
    // Any real user gesture (touch or wheel) cancels the staggered resets
    // so we never fight a legitimate scroll intent.
    window.addEventListener("wheel", markUserScrolled, { passive: true, once: true });
    window.addEventListener("touchmove", markUserScrolled, { passive: true, once: true });
    window.addEventListener("keydown", markUserScrolled, { passive: true, once: true });

    const hardReset = () => {
      if (userScrolled || cancelled) return;
      if (hash) {
        if (anchorFound) return;
        let id;
        try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
        const target = document.getElementById(id);
        if (!target) return;
        const headerHeight = document.querySelector('[data-testid="site-header"]')?.getBoundingClientRect().height || 0;
        window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - 20), behavior: 'auto' });
        anchorFound = true;
        anchorObserver?.disconnect();
        return;
      }
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    hardReset();
    const raf = requestAnimationFrame(hardReset);
    const t1 = setTimeout(hardReset, 0);
    const t2 = setTimeout(hardReset, 100);
    const t3 = setTimeout(hardReset, 220);
    if (hash && !anchorFound) {
      anchorObserver = new MutationObserver(hardReset);
      anchorObserver.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    }
    if (hash) document.fonts.ready.then(() => { if (!cancelled && !userScrolled) { anchorFound = false; hardReset(); } });
    return () => {
      cancelled = true;
      anchorObserver?.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("wheel", markUserScrolled);
      window.removeEventListener("touchmove", markUserScrolled);
      window.removeEventListener("keydown", markUserScrolled);
    };
  }, [pathname, hash]);

  const showInlineForm =
    !FORM_SKIP_EXACT.has(pathname) &&
    !FORM_SKIP_PREFIXES.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    );

  return (
    <div className="App site-shell" data-testid="app-root">
      <Header />
      <main className="site-main" data-testid="site-main">
        <Outlet />
      </main>
      {showInlineForm && (
        <InlineLeadForm
          eyebrow="Talk to Valor"
          headline={
            <>Start the conversation.</>
          }
          subhead="Tell us about your project. The Valor team will follow up with you."
          ctaLabel="Talk to Valor Team"
          testid="global-talk-to-engineering"
          theme="navy"
        />
      )}
      <Footer />
      <StickyInquiryRail />
    </div>
  );
}
